import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { theme } from '../../theme/theme'
import { TimeSlot } from './TimeSlot'
import { DayHeader } from './DayHeader'
import { isSlotSelected } from '../../utils/timeSlotCalendar'
import { useLocale } from '../../hooks/useLocale'
import type { DayTimeSlots, TimeSlot as TimeSlotType } from './types'

interface TimeSlotGridProps {
  days: DayTimeSlots[]
  onSlotSelect: (slot: TimeSlotType, date: Date) => void
  selectedSlot?: { slotId: string; date: Date }
}

export const TimeSlotGrid = ({ days, onSlotSelect, selectedSlot }: TimeSlotGridProps) => {
  const { t } = useTranslation()
  const locale = useLocale()

  return (
    <GridContainer>
      {days.map((day, dayIndex) => (
        <DayColumn key={dayIndex}>
          <DayHeader date={day.date} locale={locale} />
          {day.isFullyBooked ? (
            <FullyBookedMessage>{t('employee.healthCall.fullyBooked')}</FullyBookedMessage>
          ) : (
            <SlotsWrapper>
              {day.slots.map((slot) => (
                <TimeSlot
                  key={slot.id}
                  slot={slot}
                  isSelected={isSlotSelected(slot, day.date, selectedSlot)}
                  onClick={() => onSlotSelect(slot, day.date)}
                />
              ))}
            </SlotsWrapper>
          )}
        </DayColumn>
      ))}
    </GridContainer>
  )
}

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  grid-template-rows: 1fr;
  flex: 1;
  min-height: 0;

  @media screen and (min-width: ${theme.breakpoint.large}px) {
    gap: ${theme.spacing.small}px;
    max-height: 350px;
  }

  @media screen and (max-width: ${theme.breakpoint.large}px) {
    gap: ${theme.spacing.xsmall}px;
    max-height: 280px;
    height: 100%;
  }
`

const DayColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xsmall}px;
  min-height: 0;

  @media screen and (max-width: ${theme.breakpoint.large}px) {
    flex: 1;
    min-width: 0;
  }
`

const SlotsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xsmall}px;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
`

const FullyBookedMessage = styled.div`
  padding: ${theme.spacing.small}px;
  text-align: center;
  color: ${theme.color.grey3};
  font-size: ${theme.fontSize.xsmall}px;
  border-radius: ${theme.spacing.xsmall}px;

  @media screen and (max-width: ${theme.breakpoint.large}px) {
    padding: ${theme.spacing.small}px 0;
    font-size: ${theme.fontSize.xxsmall}px;
  }
`
